"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authClient } from "@/lib/auth-client"
import { authErrorMessage } from "@/lib/auth-errors"

// `next`: already validated by the page (safeNext), e.g. an invitation link.
export function RegisterForm({ next }: { next: string }) {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setError(null)
    setPending(true)

    const { error } = await authClient.signUp.email({
      name: String(form.get("name")).trim(),
      email: String(form.get("email")).trim(),
      password: String(form.get("password")),
    })

    if (error) {
      setError(authErrorMessage(error))
      setPending(false)
      return
    }

    // Better Auth signs the user in right after sign-up. The household
    // "Mein Zuhause" is created by the sign-up hook on the server.
    router.push(next)
    router.refresh()
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="name">Dein Name</FieldLabel>
            <Input
              id="name"
              name="name"
              type="text"
              autoComplete="given-name"
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="email">E-Mail</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Passwort</FieldLabel>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
            />
            <FieldDescription>Mindestens 8 Zeichen.</FieldDescription>
          </Field>
          {error && <FieldError>{error}</FieldError>}
          <Button type="submit" size="lg" disabled={pending}>
            Konto erstellen
          </Button>
        </FieldGroup>
      </form>

      <p className="text-center text-muted-foreground">
        Schon ein Konto?{" "}
        <Link
          href={next === "/start" ? "/login" : `/login?next=${encodeURIComponent(next)}`}
          className="inline-flex min-h-12 items-center font-medium text-foreground underline underline-offset-4"
        >
          Anmelden
        </Link>
      </p>
    </div>
  )
}
